import { PickPath } from './pickPathTypes';
import {
  PICK_PATH_OUTLINE_WIDTH,
  PICK_PATH_ARROW_SIZE,
  PICK_PATH_Z_INDEX,
  PICK_PATH_DEFAULT_COLOR,
} from './pickPathConstants';

export interface PickPathPolygonStyle {
  fillColor?: string;
  fillOpacity?: number;
  outlineColor?: string;
  outlineWidth?: number;
  outlineStyle?: 'dashed' | 'dotted' | 'solid';
  zIndex?: number;
  showArrows?: boolean;
  arrowPosition?: 'start' | 'end' | 'middle' | 'both';
  arrowSize?: number;
}

export interface PickPathFeature {
  id: string;
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: [number, number][];
  };
  properties: {
    category?: string;
    label?: string;
    style?: PickPathPolygonStyle;
    interactive?: boolean;
  };
}

export interface PickPathFeatureCollection {
  type: 'FeatureCollection';
  features: PickPathFeature[];
}

/**
 * Creates individual line segments between consecutive waypoints,
 * each with an arrow at the end. This produces arrows at every
 * waypoint along the path, matching the reference pick path style.
 */
export function buildPickPathFeatureCollection(
  paths: PickPath[],
  inProgressPath: PickPath | null,
  zoneColorMap: Map<number, string>,
): PickPathFeatureCollection {
  const features: PickPathFeature[] = [];

  // Completed paths: one segment per consecutive waypoint pair, each with an arrow
  for (const path of paths) {
    if (path.waypoints.length < 2) continue;

    const zoneColor = zoneColorMap.get(path.zoneIdx) ?? PICK_PATH_DEFAULT_COLOR;

    for (let i = 0; i < path.waypoints.length - 1; i++) {
      const from = path.waypoints[i];
      const to = path.waypoints[i + 1];
      features.push({
        id: `pick-path-${path.id}-seg-${i}`,
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [from.x, from.y],
            [to.x, to.y],
          ],
        },
        properties: {
          category: 'pickPath',
          style: {
            outlineColor: zoneColor,
            outlineWidth: PICK_PATH_OUTLINE_WIDTH,
            outlineStyle: 'solid',
            showArrows: true,
            arrowPosition: 'middle',
            arrowSize: PICK_PATH_ARROW_SIZE,
            zIndex: PICK_PATH_Z_INDEX,
          },
        },
      });
    }
  }

  // In-progress path: same segment approach but dashed
  if (inProgressPath && inProgressPath.waypoints.length >= 2) {
    const zoneColor = zoneColorMap.get(inProgressPath.zoneIdx) ?? '#FF0000';

    for (let i = 0; i < inProgressPath.waypoints.length - 1; i++) {
      const from = inProgressPath.waypoints[i];
      const to = inProgressPath.waypoints[i + 1];
      features.push({
        id: `pick-path-ip-${inProgressPath.id}-seg-${i}`,
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [from.x, from.y],
            [to.x, to.y],
          ],
        },
        properties: {
          category: 'pickPathInProgress',
          style: {
            outlineColor: zoneColor,
            outlineWidth: PICK_PATH_OUTLINE_WIDTH,
            outlineStyle: 'dashed',
            showArrows: true,
            arrowPosition: 'middle',
            arrowSize: PICK_PATH_ARROW_SIZE,
            zIndex: PICK_PATH_Z_INDEX,
          },
        },
      });
    }
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}
